import {
    ArrowDownTrayIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    DocumentIcon,
    EyeIcon,
    PlayIcon,
    XMarkIcon,
} from "@heroicons/react/24/solid";
import { forwardRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ReactComponent as AttachmentsIconOrange } from "assets/img/discussions/attachment-1.svg";
import { ReactComponent as AttachmentsIconBlue } from "assets/img/discussions/attachment-2.svg";
import {
    FileContentPreview,
    isPreviewableFile,
} from "@compass/shared/components/FileContentPreview";
import { cn } from "lib/cn";
import { humanizeBytes } from "lib/humanizeBytes";
import { trpc } from "lib/trpc";
import { ClientDocument } from "@compass/shared/types";
import { MarkdownContent } from "./MarkdownContent";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
).toString();

type FileAttachmentProps = {
    className?: string;
    file: ClientDocument;
    onRemove?: (file: ClientDocument) => void;
    /** When false, hides AI description short/long sections. Default true. */
    showAIDetails?: boolean;
};

const isAudioType = (type?: string | null) => type?.startsWith("audio/");
const isVideoType = (type?: string | null) => type?.startsWith("video/");
const isImageType = (type?: string | null) => type?.startsWith("image/");

export const FileAttachment = forwardRef(
    ({ className, file, onRemove, showAIDetails = true }: FileAttachmentProps, ref) => {
        const getDownloadUrl = trpc.userContent.getDownloadUrl.useMutation();
        const [mediaUrl, setMediaUrl] = useState<string | null>(null);
        const [loading, setLoading] = useState(false);
        const [showTranscription, setShowTranscription] = useState(false);

        const isAudio = isAudioType(file?.type);
        const isVideo = isVideoType(file?.type);
        const isImage = isImageType(file?.type);
        const isMedia = isAudio || isVideo;

        const handleLoadMedia = async () => {
            if (mediaUrl || loading) return;
            setLoading(true);
            try {
                const { downloadUrl } = await getDownloadUrl.mutateAsync({
                    fileId: file.id,
                    type: file.type,
                });
                setMediaUrl(downloadUrl);
            } finally {
                setLoading(false);
            }
        };

        const downloadDocument = async (fileId: string, type?: string | null) => {
            const { downloadUrl } = await getDownloadUrl.mutateAsync({ fileId, type });
            window.open(downloadUrl, "_blank");
        };

        if (!file) {
            return (
                <div
                    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                    ref={ref as any}
                    className={cn(
                        "flex flex-row items-center gap-2 rounded-md rounded-tl-none",
                        "border border-slate-300 bg-slate-100 p-2 text-slate-800",
                        className,
                    )}
                    contentEditable={false}
                >
                    <DocumentIcon className="h-5 w-5" />
                    <div className="text-sm font-semibold">File not found</div>
                </div>
            );
        }

        if (isMedia) {
            return (
                <div
                    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                    ref={ref as any}
                    className={cn("flex w-full flex-col gap-1", className)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                            <div className="text-sm tracking-tight text-slate-600">
                                {file.displayName || file.name}
                            </div>
                            {file.sizeBytes && (
                                <div className="text-xs text-slate-400">
                                    {humanizeBytes(file.sizeBytes)}
                                </div>
                            )}
                            {showAIDetails && file.aiDescriptionShort && (
                                <div className="text-xs text-slate-500">
                                    <MarkdownContent
                                        markdown={file.aiDescriptionShort}
                                        expanded
                                        className="prose-xs text-xs"
                                    />
                                </div>
                            )}
                        </div>
                        {onRemove && (
                            <div onClick={onRemove.bind(this, file)} className="cursor-pointer">
                                <XMarkIcon className="text-red-7 h-4 w-4" />
                            </div>
                        )}
                    </div>
                    {mediaUrl ? (
                        <div className="relative">
                            <button
                                className="absolute -top-1 -right-1 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-sm hover:bg-slate-100"
                                onClick={() => setMediaUrl(null)}
                                title="Close preview"
                            >
                                <XMarkIcon className="h-3.5 w-3.5" />
                            </button>
                            {isAudio ? (
                                <audio src={mediaUrl} controls autoPlay className="w-full" />
                            ) : (
                                <video
                                    src={mediaUrl}
                                    controls
                                    autoPlay
                                    className="max-w-full rounded"
                                />
                            )}
                        </div>
                    ) : (
                        <div
                            className="flex cursor-pointer items-center gap-2 rounded-md bg-slate-100 px-3 py-2"
                            onClick={handleLoadMedia}
                        >
                            <PlayIcon className="h-5 w-5 text-blue-600" />
                            <span className="text-sm text-blue-600">
                                {loading ? "Loading..." : `Play ${isAudio ? "audio" : "video"}`}
                            </span>
                        </div>
                    )}
                    {showAIDetails && file.aiDescriptionLong && (
                        <div className="mt-1">
                            <button
                                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                onClick={() => setShowTranscription(!showTranscription)}
                            >
                                {showTranscription ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                )}
                                {showTranscription ? "Hide" : "View full"} transcription
                            </button>
                            {showTranscription && (
                                <div className="mt-1 rounded bg-slate-50 p-2 text-xs text-slate-700">
                                    <MarkdownContent
                                        markdown={file.aiDescriptionLong}
                                        expanded
                                        className="prose-xs text-xs"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        if (isImage) {
            return (
                <div
                    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                    ref={ref as any}
                    className={cn("flex w-full flex-col gap-1", className)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                            <div className="text-sm tracking-tight text-slate-600">
                                {file.displayName || file.name}
                            </div>
                            {file.sizeBytes && (
                                <div className="text-xs text-slate-400">
                                    {humanizeBytes(file.sizeBytes)}
                                </div>
                            )}
                            {showAIDetails && file.aiDescriptionShort && (
                                <div className="text-xs text-slate-500">
                                    <MarkdownContent
                                        markdown={file.aiDescriptionShort}
                                        expanded
                                        className="prose-xs text-xs"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {mediaUrl && (
                                <div
                                    className="cursor-pointer"
                                    onClick={() => downloadDocument(file.id, file.type)}
                                >
                                    <ArrowDownTrayIcon className="h-4 w-4 text-blue-600" />
                                </div>
                            )}
                            {onRemove && (
                                <div onClick={onRemove.bind(this, file)} className="cursor-pointer">
                                    <XMarkIcon className="text-red-7 h-4 w-4" />
                                </div>
                            )}
                        </div>
                    </div>
                    {mediaUrl ? (
                        <div className="relative">
                            <button
                                className="absolute top-1 right-1 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-sm hover:bg-slate-100"
                                onClick={() => setMediaUrl(null)}
                                title="Close preview"
                            >
                                <XMarkIcon className="h-3.5 w-3.5" />
                            </button>
                            <img
                                src={mediaUrl}
                                alt={file.aiDescriptionShort || file.displayName || file.name || ""}
                                className="max-w-full rounded"
                            />
                        </div>
                    ) : (
                        <div
                            className="flex cursor-pointer items-center gap-2 rounded-md bg-slate-100 px-3 py-2"
                            onClick={handleLoadMedia}
                        >
                            <EyeIcon className="h-5 w-5 text-blue-600" />
                            <span className="text-sm text-blue-600">
                                {loading ? "Loading..." : "View image"}
                            </span>
                        </div>
                    )}
                    {showAIDetails && file.aiDescriptionLong && (
                        <div className="mt-1">
                            <button
                                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                onClick={() => setShowTranscription(!showTranscription)}
                            >
                                {showTranscription ? (
                                    <ChevronUpIcon className="h-3 w-3" />
                                ) : (
                                    <ChevronDownIcon className="h-3 w-3" />
                                )}
                                {showTranscription ? "Hide" : "View"} AI description
                            </button>
                            {showTranscription && (
                                <div className="mt-1 rounded bg-slate-50 p-2 text-xs text-slate-700">
                                    <MarkdownContent
                                        markdown={file.aiDescriptionLong}
                                        expanded
                                        className="prose-xs text-xs"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                ref={ref as any}
                className={cn(
                    "group flex w-full flex-col gap-1",
                    "text-slate-800",
                    "bg-white",
                    className,
                )}
            >
                <div className="flex flex-row items-center justify-between">
                    <div
                        className="flex flex-row items-center"
                        onClick={() => downloadDocument?.(file.id, file.type)}
                    >
                        <div className="mr-2 cursor-pointer rounded-md ">
                            {file.type?.includes("zip") ? (
                                <AttachmentsIconOrange className="h-10 w-10 shrink-0" />
                            ) : (
                                <AttachmentsIconBlue className="h-10 w-10 shrink-0" />
                            )}
                        </div>
                        <div className="flex flex-col">
                            <div className="cursor-pointer text-sm tracking-tight text-[#2ca8de] hover:underline">
                                {file.displayName || file.name}
                            </div>
                            {file.sizeBytes && (
                                <div className="text-xs text-slate-400">
                                    {humanizeBytes(file.sizeBytes)}
                                </div>
                            )}
                        </div>
                    </div>
                    {onRemove && (
                        <div onClick={onRemove.bind(this, file)} className="cursor-pointer">
                            <XMarkIcon className="text-red-7 h-4 w-4" />
                        </div>
                    )}
                </div>
                {showAIDetails && file.aiDescriptionShort && (
                    <div className="text-xs text-slate-500">
                        <MarkdownContent
                            markdown={file.aiDescriptionShort}
                            expanded
                            className="prose-xs text-xs"
                        />
                    </div>
                )}
                {showAIDetails && file.aiDescriptionLong && (
                    <div className="mt-1">
                        <button
                            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            onClick={() => setShowTranscription(!showTranscription)}
                        >
                            {showTranscription ? (
                                <ChevronUpIcon className="h-3 w-3" />
                            ) : (
                                <ChevronDownIcon className="h-3 w-3" />
                            )}
                            {showTranscription ? "Hide" : "View"} AI description
                        </button>
                        {showTranscription && (
                            <MarkdownContent
                                markdown={file.aiDescriptionLong}
                                expanded
                                className="text-slate-700s mt-1 whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs"
                            />
                        )}
                    </div>
                )}
                {showAIDetails && isPreviewableFile(file.type, file.name) && (
                    <FileContentPreview
                        fileType={file.type}
                        fileName={file.name}
                        fetchContent={async () => {
                            const { downloadUrl } = await getDownloadUrl.mutateAsync({
                                fileId: file.id,
                                type: file.type,
                            });
                            const res = await fetch(downloadUrl);
                            return res.text();
                        }}
                        fetchUrl={async () => {
                            const { downloadUrl } = await getDownloadUrl.mutateAsync({
                                fileId: file.id,
                                type: file.type,
                            });
                            return downloadUrl;
                        }}
                        renderPDF={(url) => <InlinePDFPreview url={url} />}
                    />
                )}
            </div>
        );
    },
);

const InlinePDFPreview: React.FC<{ url: string }> = ({ url }) => {
    const [numPages, setNumPages] = useState(0);
    return (
        <Document
            file={url}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={<span className="p-2 text-xs text-slate-500">Loading PDF...</span>}
        >
            {Array.from({ length: Math.min(numPages, 5) }, (_, i) => (
                <Page key={i + 1} pageNumber={i + 1} width={500} />
            ))}
            {numPages > 5 && (
                <p className="p-2 text-center text-xs text-slate-400">
                    Showing 5 of {numPages} pages
                </p>
            )}
        </Document>
    );
};
